import { MouseEvent, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { fetchClientsMissingNotificationsRecipients } from '../../features/EDIContainer/EDIContainerActions';
import { selectClientsMissingNotificationRecipientsList, selectError } from '../../features/EDIContainer/EDIContainerSlice';
import { Skeleton, Grid, Typography, ListItemText } from '@mui/material';
import { ClickableDashboardCard, DashboardCardContent, DashboardRootCardHeader } from '../../util/SharedStyles';
import { ClientModel } from '../../gql-types.generated';
import { Viewer } from '../../util/Constants';
import ClientsMissingNotificationRecipientsDialog from '../dialogs/ClientsMissingNotificationRecipientsDialog';

interface MissingRecipientsCardProps {
    viewer?: Viewer | undefined;
}

const ClientsMissingRecipientsCard: React.FC<MissingRecipientsCardProps> = (props: MissingRecipientsCardProps) => {
    const { viewer } = props;
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const cardRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [clientCount, setClientCount] = useState<number>(0);
    const [singleClient, setSingleClient] = useState<ClientModel | undefined>(undefined);
    const [openDialog, setOpenDialog] = useState<boolean>(false);
        
    const clientsList = useAppSelector(selectClientsMissingNotificationRecipientsList);
    const error = useAppSelector(selectError);

    useEffect(() => {
        dispatch(fetchClientsMissingNotificationsRecipients());
    }, []);

    useEffect(() => {
        if (error) {
            setLoading(false);
        } else if (clientsList) {
            setLoading(false);
            let count = clientsList.length ?? 0;
            setClientCount(count);

            if (count === 1) {
                setSingleClient(clientsList[0]);
            }
        } else {
            setLoading(true);
        }
    }, [clientsList, error]);

    const cardClick = () => {
        if (!loading) {
            if (clientCount > 0) {
                if (singleClient) {
                    // navigate to the particular client
                    let url = '/error/unknowntype';
                    
                    // clientsNoRecipients are displayed for Clients
                    if (singleClient.id){
                        url = '/client/' + singleClient.id;
                    }
                    
                    navigate(url);
                } else {
                    // open dialog for clientsNoRecipients list
                    setOpenDialog(true);
                }
            }
        }
    };

    const getCardContent = () => {
        if (error) {
            return (
                <Typography variant="body1" color="error">
                   Unable to load.
                </Typography>
            );
        } else {
            if (singleClient) {
                return (
                    <Grid item>
                        <ListItemText primary={singleClient.name} secondary={singleClient.lastModifiedByName} />
                    </Grid>
                );
            } else {
                var color = clientCount > 0 ? "primary" : "text";
                return (
                    <Typography variant="h3" color={color}>
                        {clientCount}
                    </Typography>
                );
            }
        }
    };

    const onDialogClose = (event?: MouseEvent<HTMLElement>) => {
        // this button could be inside of a card action area that has a click event,
        // stopPropagation to prevent that click event from firing
        event?.stopPropagation();

        // need to wrap in timeout, otherwise dialog doesn't receive the changed state
        setTimeout(() => {
            setOpenDialog(false);
        });
    };

    const getOffsetTop = () => {
        // want the dialog to display right below the card
        // so use height and top to get the top for dialog
        if (cardRef?.current) {
            const { offsetHeight, offsetTop } = cardRef.current;
            if (offsetTop > offsetHeight) {
                return offsetTop - offsetHeight;
            } else {
                return offsetTop + offsetHeight;
            }
        }
        return 0;
    };

    const getDialogHeight = () => {
        if (cardRef?.current) {
            const { offsetParent } = cardRef.current;
            if (offsetParent && offsetParent.clientHeight) {
                let headerAndCardHeight = 250;
                return `${offsetParent.clientHeight - headerAndCardHeight}px`;
            } 
        }
        return '600px';
    };
    
    return (
        <ClickableDashboardCard ref={cardRef} onClick={cardClick} disabled={loading || clientCount < 1}>
            <DashboardRootCardHeader
                title={
                    loading ? (
                        <Skeleton animation="wave" height={32} width="60%" />
                    ) : (
                        <Typography variant="h6">Missing Recipients</Typography>
                    )
                }
            />
            <DashboardCardContent>
                {
                    loading ? (
                        <Grid container item justifyContent={"center"}>
                            <Skeleton animation="wave" variant="circular" width={40} height={40} />
                        </Grid>
                    ) : (
                        <Grid container item justifyContent={"center"}>
                            {getCardContent()}
                        </Grid>
                    )
                }
            </DashboardCardContent>
            <ClientsMissingNotificationRecipientsDialog 
                isOpen={openDialog} 
                viewer={viewer}
                clients={clientsList ?? []}
                onClose={onDialogClose}
                maxWidth='md'
                height={getDialogHeight()}
                offsetLeft={cardRef?.current?.offsetLeft ?? 0}
                offsetTop={getOffsetTop()}
                error={error}
            />
        </ClickableDashboardCard>
    )
};

export default ClientsMissingRecipientsCard;